import React, { useMemo } from 'react';

import Archive from '@mui/icons-material/Archive';
import Unarchive from '@mui/icons-material/Unarchive';

// components
import BasicButton from '../../components/buttons/basicButton';
import AampeTooltip from '../../components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';
// constants
import {
    CONTROLLED_ARCHIVED_FORMULA_SCHEDULED_DATE_PASS_TOOLTIP,
    CONTROLLED_FORMULA_CANCEL_BUFFER,
    CONTROLLED_FORMULA_EXCEED_BUFFER_TIME_TOOLTIP,
} from '../../constants/formula';

// helpers
import confirm from '../../helpers/confirm';
import { hasScheduleTimeExceededBufferTime } from '../../helpers/formulaHelpers';
import { isScheduleTimeInPast } from '../../helpers/formulaHelpersPure';

import useFormulaArchive from '../../hooks/formulas/useFormulaArchive';
// hooks
import useFormulaDraft from '../../hooks/formulas/useFormulaDraft';
import useAnalytics from '../../hooks/helper/useAnalytics';
import useRoleFlags from 'hooks/auth/useRoleFlags';

// models
import {
    ApprovedFormulaState,
    FormulaDetailModel,
} from '../../models/formula.model';

interface FormulaViewActionsProps {
    isFormulaArchived: boolean;
    formulaId: number;
    formula: FormulaDetailModel;
}

const FormulaViewActions: React.FC<
    React.PropsWithChildren<FormulaViewActionsProps>
> = ({ isFormulaArchived, formulaId, formula }) => {
    const { hasWriteAccess } = useRoleFlags();
    const archiveFormula = useFormulaArchive();
    const draftFormula = useFormulaDraft(formulaId);

    const { track } = useAnalytics();

    const trackUnarchive = () => {
        track('formula_unarchive_button_clicked', {
            id: formulaId,
        });
    };

    const trackEdit = () => {
        track('formula_edit_button_clicked', {
            id: formulaId,
        });
    };

    const handleEdit = async () => {
        const activeState = formula.active_state;
        let finalWarning = 'This will return your message to DRAFT mode.';
        if (
            formula.is_controlled_message &&
            [
                ApprovedFormulaState.Live,
                ApprovedFormulaState.Scheduled,
            ].includes(activeState as ApprovedFormulaState)
        ) {
            finalWarning =
                'This will cancel your scheduled message and move it to draft mode';
        }
        const confirmed = await confirm(
            isFormulaArchived
                ? 'Unarchiving a message'
                : 'Editing an approved message',
            undefined,
            finalWarning,
            undefined,
            'Unarchive',
        );
        if (formulaId && confirmed) {
            if (isFormulaArchived) {
                trackUnarchive();
            } else {
                trackEdit();
            }
            draftFormula.mutate({});
        }
    };

    const isScheduleTimeExceededBufferTime = useMemo<boolean>(() => {
        const activeState = formula.active_state;
        if (
            formula.is_controlled_message &&
            ApprovedFormulaState.Live === activeState
        ) {
            return hasScheduleTimeExceededBufferTime(
                formula?.send_start as string,
                CONTROLLED_FORMULA_CANCEL_BUFFER,
            );
        }
        return false;
    }, [formula]);

    const isUnarchiveControlledFormulaDisabled = useMemo<boolean>(
        () =>
            !!(
                formula.is_controlled_message &&
                isFormulaArchived &&
                isScheduleTimeInPast(formula?.send_start as string, 0)
            ),
        [formula, isFormulaArchived],
    );

    const handleArchive = async () => {
        const confirmed = await confirm(
            'Are you sure you want to archive this message?',
            undefined,
            undefined,
            undefined,
            'Archive',
        );
        if (formulaId && confirmed) {
            archiveFormula.mutate(formulaId, {
                onError: () => {
                    alert('Unable to archive');
                },
            });
        }
    };

    const isExpiredFormula = useMemo(
        () => ApprovedFormulaState.Expired === formula?.active_state,
        [formula],
    );

    const isDisabled = useMemo<boolean>(() => {
        return !hasWriteAccess;
    }, [hasWriteAccess]);

    return (
        <>
            <Container>
                {isFormulaArchived ? (
                    <AampeTooltip
                        title={
                            isUnarchiveControlledFormulaDisabled
                                ? CONTROLLED_ARCHIVED_FORMULA_SCHEDULED_DATE_PASS_TOOLTIP
                                : 'Pressing this button will move this message from the archived catalog to the draft catalog.'
                        }
                        arrow
                    >
                        <div>
                            <BasicButton
                                name="Unarchive"
                                action={handleEdit}
                                isPrimary={false}
                                style={{
                                    margin: aampeTheme.spacing(0, 0.5),
                                }}
                                isLoading={draftFormula.isPending}
                                icon={
                                    <Unarchive style={{ fontSize: '16px' }} />
                                }
                                disabled={
                                    isUnarchiveControlledFormulaDisabled ||
                                    isDisabled
                                }
                            />
                        </div>
                    </AampeTooltip>
                ) : (
                    <>
                        {isExpiredFormula && (
                            <AampeTooltip
                                title={
                                    isScheduleTimeExceededBufferTime
                                        ? CONTROLLED_FORMULA_EXCEED_BUFFER_TIME_TOOLTIP
                                        : 'Pressing this button will stop sending all of the variants of this message group and move them to the archived catalog.'
                                }
                                arrow
                            >
                                <div>
                                    <BasicButton
                                        name="Archive"
                                        isPrimary={false}
                                        disabled={
                                            archiveFormula.isPending ||
                                            isScheduleTimeExceededBufferTime ||
                                            isDisabled
                                        }
                                        action={handleArchive}
                                        style={{
                                            margin: aampeTheme.spacing(0, 0.5),
                                        }}
                                        icon={
                                            <Archive
                                                style={{ fontSize: '16px' }}
                                            />
                                        }
                                    />
                                </div>
                            </AampeTooltip>
                        )}
                    </>
                )}
            </Container>
        </>
    );
};

const Container = styled.div(() => ({
    display: 'flex',
    gap: aampeTheme.spacing(1),
    alignItems: 'center',
}));

export default FormulaViewActions;
